import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, Share2, Twitter, Linkedin, Home } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import ReactMarkdown from 'react-markdown';
import { format } from 'date-fns';
import { BlogPost } from '../types/blog';
import { getPostBySlug } from '../lib/blog';

export function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { theme } = useTheme();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [activeSection, setActiveSection] = useState<string>('');
  
  useEffect(() => {
    const loadPost = async () => {
      if (slug) {
        const postData = await getPostBySlug(slug);
        setPost(postData);
      }
    };
    loadPost();
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      const headings = document.querySelectorAll('h1, h2, h3');
      let current = '';

      headings.forEach((heading) => {
        const top = heading.getBoundingClientRect().top;
        if (top < 100) {
          current = heading.id;
        }
      });

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!post) {
    return (
      <div className="py-32">
        <div className="max-w-[1440px] mx-auto px-6">
          <h1 className="text-3xl">Post not found</h1>
        </div>
      </div>
    );
  }

  // Extract headings from content for table of contents
  const headings = post.content.split('\n')
    .filter(line => line.startsWith('#'))
    .map(line => {
      const match = line.match(/^#+/);
      const level = match ? match[0].length : 1;
      const text = line.replace(/^#+\s+/, '');
      const id = text.toLowerCase().replace(/[^\w]+/g, '-');
      return { level, text, id };
    });

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-dark-100' : 'bg-gray-50'}`}>
      {/* Breadcrumb Navigation */}
      <div className={`sticky top-0 z-50 w-full border-b ${
        theme === 'dark' ? 'bg-dark-200/80 backdrop-blur-md border-dark-300' : 'bg-white/80 backdrop-blur-md border-gray-200'
      }`}>
        <div className="max-w-[1440px] mx-auto px-6 py-4">
          <div className="flex items-center space-x-3 text-sm">
            <Link to="/" className="text-primary-600 hover:text-primary-700">
              <Home className="w-4 h-4" />
            </Link>
            <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
              /
            </span>
            <Link to="/blog" className="text-primary-600 hover:text-primary-700">
              Blog
            </Link>
            <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
              /
            </span>
            <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
              {post.title}
            </span>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <header className={`w-full border-b ${
        theme === 'dark' ? 'bg-dark-200 border-dark-300' : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-[1440px] mx-auto px-6 py-16">
          <div className="max-w-4xl">
            <div className="flex items-center gap-4 mb-6 text-sm">
              <span className={`inline-flex items-center ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <Calendar className="w-4 h-4 mr-1" />
                {format(new Date(post.date), 'MMM d, yyyy')}
              </span>
              <span className={`inline-flex items-center ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <Clock className="w-4 h-4 mr-1" />
                {post.readingTime}
              </span>
            </div>
            <h1 className="text-5xl font-normal mb-8 leading-tight">{post.title}</h1>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <div>
                  <div className="font-medium">{post.author}</div>
                  <div className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Author
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                {post.tags.map((tag, index) => (
                  <span
                    key={index}
                    className={`text-sm px-3 py-1 rounded-full ${
                      theme === 'dark'
                        ? 'bg-dark-300 text-gray-300'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1440px] mx-auto px-6 py-16">
        <div className="grid grid-cols-12 gap-16">
          {/* Table of Contents Sidebar */}
          <aside className="col-span-3">
            <div className="sticky top-24">
              <h2 className={`text-lg font-medium mb-4 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
              }`}>
                In this story
              </h2>
              <nav className="space-y-1">
                {headings.map((heading, index) => (
                  <a
                    key={index}
                    href={`#${heading.id}`}
                    className={`block py-2 text-sm transition-colors duration-200 ${
                      activeSection === heading.id
                        ? 'text-primary-600'
                        : theme === 'dark'
                          ? 'text-gray-400 hover:text-gray-200'
                          : 'text-gray-600 hover:text-gray-900'
                    } ${heading.level === 1 ? 'font-medium' : 'pl-4'}`}
                  >
                    {heading.text}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Article Content */}
          <article className="col-span-9">
            {/* Featured Image */}
            <div className="mb-12 rounded-2xl overflow-hidden">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-[400px] object-cover"
              />
            </div>

            {/* Content */}
            <div className={`prose ${
              theme === 'dark' ? 'prose-invert' : ''
            } prose-lg max-w-4xl prose-headings:scroll-mt-32 prose-headings:font-normal prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl prose-h4:text-xl prose-headings:mb-6 prose-p:mb-6 prose-p:leading-relaxed prose-a:text-primary-600 hover:prose-a:text-primary-700 prose-blockquote:border-l-4 prose-blockquote:border-primary-500 prose-blockquote:pl-6 prose-blockquote:italic prose-strong:font-medium prose-ul:my-6 prose-li:my-2`}>
              <ReactMarkdown>{post.content}</ReactMarkdown>
            </div>

            {/* Share Section */}
            <div className="mt-16 pt-16 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Share this article
                </div>
                <div className="flex items-center gap-4">
                  <button className={`p-2 rounded-full transition-colors duration-300 ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-dark-300'
                      : 'text-gray-600 hover:text-primary-600 hover:bg-gray-100'
                  }`}>
                    <Twitter className="w-5 h-5" />
                  </button>
                  <button className={`p-2 rounded-full transition-colors duration-300 ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-dark-300'
                      : 'text-gray-600 hover:text-primary-600 hover:bg-gray-100'
                  }`}>
                    <Linkedin className="w-5 h-5" />
                  </button>
                  <button className={`p-2 rounded-full transition-colors duration-300 ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-dark-300'
                      : 'text-gray-600 hover:text-primary-600 hover:bg-gray-100'
                  }`}>
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </article>
        </div>
      </main>
    </div>
  );
}